import { ReactNode, useMemo, useState } from 'react'
import { Loader2, Settings2Icon } from 'lucide-react'
import EventActivityNotesContent, {
  EventActivityNotesControl,
} from './EventActivityNotes'
import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import { hasEventPlanAdminAccess } from 'app/frontend/base/config/permissions'
import {
  usePageInfo,
  useRaidPlans,
  useVisualPlans,
} from 'app/frontend/base/config/queries'
import { Badge } from '@/components/ui/badge'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import { cn } from '@/lib/utils'
import { EventActivityLinkedPlanData } from '../hooks/useEventActivityLinkedPlanData'
import { EventPlanOverridesByPlanId } from './eventActivityPlanOverrides'
import { diffPlanOverrides } from './eventActivityPlanOverridesPatch'
import EventActivityBlock from './EventActivityBlock'
import EventActivitySelectionCount from './EventActivitySelectionCount'
import { hasLinkedRaidPlanRefs } from './linkedPlanReveal'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import EventActivityHeaderContextMenu from './EventActivityHeaderContextMenu'
import { useCopyToClipboard } from 'app/frontend/base/hooks/useCopyToClipboard'
import { buildEventActivityLinkedPlanNote } from './eventActivityNoteExport'
import LinkActivityPlanDialog, {
  LinkActivityPlanDialogMode,
} from './LinkActivityPlanDialog'
import EventActivityLinkedPlanList from './EventActivityLinkedPlanList'
import { LinkPlanEncounterOption } from './LinkPlanEncounterCard'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import VisualPlan from 'app/frontend/types/custom/VisualPlan'

type LinkDialogState = {
  activityId: number
  mode: LinkActivityPlanDialogMode
}

type Props = {
  activity: wowaudit.EventActivity
  activities: wowaudit.EventActivity[]
  allSelectedForActivity: boolean
  linkedPlanData?: EventActivityLinkedPlanData
  linkedPlanDataByActivityId?: Map<number, EventActivityLinkedPlanData>
  isLinkedPlanDataLoading: boolean
  isPlanningInfoLoading?: boolean
  titleSlot?: ReactNode
  allEncounters?: boolean
}

const sortCandidatePlans = <T extends { difficulty: string; name: string }>(
  plans: T[],
  activityDifficulty: string,
) =>
  [...plans].sort((left, right) => {
    if (left.difficulty !== right.difficulty) {
      if (left.difficulty === activityDifficulty) return -1
      if (right.difficulty === activityDifficulty) return 1
      return difficultiesLabels[
        left.difficulty as keyof typeof difficultiesLabels
      ].localeCompare(
        difficultiesLabels[right.difficulty as keyof typeof difficultiesLabels],
      )
    }
    return left.name.localeCompare(right.name)
  })

const EventActivityPlan = ({
  activity,
  activities,
  allSelectedForActivity,
  linkedPlanData,
  linkedPlanDataByActivityId,
  isLinkedPlanDataLoading,
  isPlanningInfoLoading = false,
  titleSlot,
  allEncounters,
}: Props) => {
  const { guild, team } = useTeam()
  const { accessLevel } = useAuth()
  const canEditEvent = accessLevel === 'admin'
  const canEditPlans = hasEventPlanAdminAccess(accessLevel, guild, team.id)
  const {
    data: { currentSeason, gameVersion },
  } = usePageInfo()
  const { updateEventActivity, exportEventActivityOverrides, isSaving } =
    useTeamEventUpdateContext()
  const { allEntries, event } = useTeamEventParticipantsContext()
  const showActivityNotes =
    event.raidsPlanningScope === 'per_encounter' && !allEncounters
  const { copied, copyToClipboard } = useCopyToClipboard()
  const [linkDialogState, setLinkDialogState] =
    useState<LinkDialogState | null>(null)
  const [isEditingActivityNotes, setIsEditingActivityNotes] = useState(false)
  const { data: raidPlans } = useRaidPlans(
    { ownerType: 'team', ownerId: team.id },
    currentSeason?.id,
  )
  const { data: visualPlans } = useVisualPlans(
    { ownerType: 'team', ownerId: team.id },
    currentSeason?.id,
  )
  const linkedPlans = linkedPlanData?.linkedPlans ?? []
  const linkedRaidPlans = linkedPlanData?.raidPlans ?? []
  const linkedPlanRefs = activity.linkedPlans ?? []
  const linkedRaidPlanIds = linkedPlanRefs
    .filter((entry) => entry.type === 'raid')
    .map((entry) => entry.id)
  const linkedVisualPlanIds = linkedPlanRefs
    .filter((entry) => entry.type === 'visual')
    .map((entry) => entry.id)
  const hasAnyLinkedPlans = linkedPlanRefs.length > 0
  const canReorderLinkedPlans = linkedPlans.length > 1
  const encounterActivities = useMemo(
    () => activities.filter((entry) => entry.encounter?.id != null),
    [activities],
  )
  const linkDialogActivity = useMemo(
    () =>
      activities.find((entry) => entry.id === linkDialogState?.activityId) ??
      activity,
    [activities, activity, linkDialogState?.activityId],
  )
  const linkDialogEncounterId = linkDialogActivity.encounter?.id
  const candidateRaidPlans = useMemo(() => {
    if (!linkDialogEncounterId) return []
    return sortCandidatePlans(
      (raidPlans ?? []).filter(
        (raidPlan) => raidPlan.encounterId === linkDialogEncounterId,
      ),
      linkDialogActivity.difficulty,
    )
  }, [linkDialogActivity.difficulty, linkDialogEncounterId, raidPlans])
  const candidateVisualPlans = useMemo(() => {
    if (!linkDialogEncounterId) return []
    return sortCandidatePlans(
      (visualPlans ?? []).filter(
        (visualPlan) => visualPlan.encounterId === linkDialogEncounterId,
      ),
      linkDialogActivity.difficulty,
    )
  }, [linkDialogActivity.difficulty, linkDialogEncounterId, visualPlans])
  const linkDialogLinkedPlanRefs = linkDialogActivity.linkedPlans ?? []
  const linkDialogLinkedRaidPlanIds = linkDialogLinkedPlanRefs
    .filter((entry) => entry.type === 'raid')
    .map((entry) => entry.id)
  const linkDialogLinkedVisualPlanIds = linkDialogLinkedPlanRefs
    .filter((entry) => entry.type === 'visual')
    .map((entry) => entry.id)
  const linkDialogEncounters = useMemo((): LinkPlanEncounterOption[] => {
    if (!allEncounters) return []
    return encounterActivities.flatMap((entry) => {
      const encounter = entry.encounter
      if (!encounter) return []
      return [
        {
          activityId: entry.id,
          encounterId: encounter.id,
          difficulty: entry.difficulty,
          name: encounter.name,
          thumbnailUrl: encounter.thumbnailUrl,
          instanceTileUrl: entry.instance?.tileUrl,
          activityType: entry.activityType,
          linkedPlanCount: entry.linkedPlans?.length ?? 0,
        },
      ]
    })
  }, [allEncounters, encounterActivities])
  const selectedParticipants = linkedPlanData?.selectedParticipants ?? []
  const planOverrides =
    (activity.planOverrides as EventPlanOverridesByPlanId) ?? {}
  const copiedNote = copied !== undefined
  const allEncountersHaveAnyLinkedPlans = useMemo(() => {
    if (!allEncounters || !linkedPlanDataByActivityId) return false
    return encounterActivities.some((entry) => {
      const data = linkedPlanDataByActivityId.get(entry.id)
      return (data?.linkedPlans.length ?? 0) > 0
    })
  }, [allEncounters, encounterActivities, linkedPlanDataByActivityId])
  const showLinkButton =
    canEditPlans &&
    gameVersion === 'live' &&
    (allEncounters
      ? encounterActivities.length > 0
      : !!activity.encounter && activity.active)

  const handleCopyNote = () => {
    const note = buildEventActivityLinkedPlanNote({
      activity,
      linkedPlanData,
      allEntries,
      planAbilityDefaults: team.planAbilityDefaults,
    })
    if (!note) return
    copyToClipboard(note)
  }

  const handlePlanOverridesChange = (
    targetActivity: wowaudit.EventActivity,
    nextPlanOverrides: EventPlanOverridesByPlanId,
  ) => {
    const currentPlanOverrides =
      (targetActivity.planOverrides as EventPlanOverridesByPlanId) ?? {}
    const diff = diffPlanOverrides(currentPlanOverrides, nextPlanOverrides)
    if (diff.kind === 'noop') return

    void updateEventActivity(
      targetActivity.id,
      diff.kind === 'patch'
        ? { planOverridesPatch: diff.patch }
        : { planOverrides: nextPlanOverrides },
      nextPlanOverrides,
    )
  }

  const handleSelectRaidPlan = (plan: RaidPlan) => {
    const state = linkDialogState
    if (!state) return
    const targetActivity =
      activities.find((entry) => entry.id === state.activityId) ?? activity
    const currentLinkedPlans = targetActivity.linkedPlans ?? []
    const replacePlanId =
      state.mode.kind === 'replace' && state.mode.planType === 'raid'
        ? state.mode.planId
        : null
    const canReplace =
      replacePlanId != null &&
      currentLinkedPlans.some(
        (entry) => entry.type === 'raid' && entry.id === replacePlanId,
      )
    const nextLinkedPlans = canReplace
      ? currentLinkedPlans.map((entry) =>
          entry.type === 'raid' && entry.id === replacePlanId
            ? { type: 'raid' as const, id: plan.id }
            : entry,
        )
      : [{ type: 'raid' as const, id: plan.id }, ...currentLinkedPlans]
    setLinkDialogState(null)
    void updateEventActivity(targetActivity.id, {
      linkedPlans: nextLinkedPlans,
    })
  }

  const handleSelectVisualPlan = (plan: VisualPlan) => {
    const state = linkDialogState
    if (!state) return
    const targetActivity =
      activities.find((entry) => entry.id === state.activityId) ?? activity
    const currentLinkedPlans = targetActivity.linkedPlans ?? []
    const replacePlanId =
      state.mode.kind === 'replace' && state.mode.planType === 'visual'
        ? state.mode.planId
        : null
    const canReplace =
      replacePlanId != null &&
      currentLinkedPlans.some(
        (entry) => entry.type === 'visual' && entry.id === replacePlanId,
      )
    const nextLinkedPlans = canReplace
      ? currentLinkedPlans.map((entry) =>
          entry.type === 'visual' && entry.id === replacePlanId
            ? { type: 'visual' as const, id: plan.id }
            : entry,
        )
      : [{ type: 'visual' as const, id: plan.id }, ...currentLinkedPlans]
    setLinkDialogState(null)
    void updateEventActivity(targetActivity.id, {
      linkedPlans: nextLinkedPlans,
    })
  }

  const unlinkPlan = (
    targetActivity: wowaudit.EventActivity,
    planType: 'raid' | 'visual',
    planId: number,
  ) => {
    const state = linkDialogState
    const currentLinkedPlans = targetActivity.linkedPlans ?? []
    void updateEventActivity(targetActivity.id, {
      linkedPlans: currentLinkedPlans.filter(
        (entry) => !(entry.type === planType && entry.id === planId),
      ),
    })
    if (
      state?.activityId === targetActivity.id &&
      state.mode.kind === 'replace' &&
      state.mode.planType === planType &&
      state.mode.planId === planId
    ) {
      setLinkDialogState(null)
    }
  }

  const moveLinkedPlan = (
    targetActivity: wowaudit.EventActivity,
    planType: 'raid' | 'visual',
    planId: number,
    direction: -1 | 1,
  ) => {
    const currentLinkedPlans = targetActivity.linkedPlans ?? []
    const index = currentLinkedPlans.findIndex(
      (entry) => entry.type === planType && entry.id === planId,
    )
    if (index < 0) return
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= currentLinkedPlans.length) return
    const nextLinkedPlans = [...currentLinkedPlans]
    const [moved] = nextLinkedPlans.splice(index, 1)
    nextLinkedPlans.splice(targetIndex, 0, moved)
    void updateEventActivity(targetActivity.id, {
      linkedPlans: nextLinkedPlans,
    })
  }

  const openAddLinkDialog = () => {
    if (allEncounters) {
      const firstEncounterActivity = encounterActivities[0]
      if (!firstEncounterActivity) return
      setLinkDialogState({
        activityId: firstEncounterActivity.id,
        mode: { kind: 'add' },
      })
      return
    }
    setLinkDialogState({
      activityId: activity.id,
      mode: { kind: 'add' },
    })
  }

  const renderEncounterTitlePrefix = (
    encounterActivity: wowaudit.EventActivity,
  ) => {
    const encounter = encounterActivity.encounter
    if (!encounter) return null
    return (
      <span className='flex items-center gap-1.5 min-w-0 shrink-0'>
        <img
          className='w-5 h-5 rounded border border-border shrink-0'
          src={
            encounter.thumbnailUrl ??
            encounterActivity.instance?.tileUrl ??
            undefined
          }
        />
        <h4 className='m-0 truncate transition-colors group-hover/title:text-accent-foreground'>
          {encounter.name}
        </h4>
        <span className='text-muted-foreground/70'>·</span>
      </span>
    )
  }

  return (
    <Card className='mt-2 p-4'>
      <div className='flex items-center justify-between gap-2 mb-4 flex-wrap'>
        <div className='flex items-center gap-3 flex-wrap'>
          {titleSlot ?? (
            <>
              <img
                className={cn(
                  'w-8 h-8 rounded-md border-2 border-border shrink-0 transition-colors',
                  !activity.active && 'grayscale',
                )}
                src={
                  activity.encounter?.thumbnailUrl ??
                  activity.instance?.tileUrl ??
                  ''
                }
              />
              <h3 className='m-0'>
                {activity.encounter?.name || activity.instance?.name}
              </h3>
            </>
          )}
          <Badge variant={activity.active ? 'outline' : 'warning'}>
            {activity.active
              ? difficultiesLabels[activity.difficulty]
              : 'Inactive'}
          </Badge>
        </div>

        <div className='flex items-center gap-2 flex-wrap justify-end'>
          {allEncounters ? null : (
            <>
              {showActivityNotes ? (
                <EventActivityNotesControl
                  activity={activity}
                  className='h-9'
                  canEdit={canEditEvent}
                  isEditing={isEditingActivityNotes}
                  onStartEdit={() => setIsEditingActivityNotes(true)}
                />
              ) : null}
              {activity.active ? (
                <Button
                  size='sm'
                  variant={linkedRaidPlans.length > 0 ? 'default' : 'outline'}
                  className={cn(
                    'transition-opacity duration-300',
                    hasAnyLinkedPlans &&
                      isLinkedPlanDataLoading &&
                      linkedPlans.length === 0 &&
                      'opacity-0 pointer-events-none',
                    linkedRaidPlans.length > 0 &&
                      'flex flex-col h-auto py-1 leading-tight',
                  )}
                  onClick={handleCopyNote}
                >
                  {linkedRaidPlans.length > 0 ? (
                    <>
                      <span>{copiedNote ? 'Copied!' : 'Copy note'}</span>
                      <span className='text-xs opacity-70'>and invites</span>
                    </>
                  ) : (
                    'Copy invites'
                  )}
                </Button>
              ) : canEditEvent ? (
                <Button
                  size='sm'
                  onClick={() =>
                    void updateEventActivity(activity.id, { active: true })
                  }
                >
                  Set active
                </Button>
              ) : null}
            </>
          )}

          {canEditEvent || showLinkButton ? (
            <>
              {showLinkButton ? (
                <Button
                  size='sm'
                  className={cn(
                    'transition-opacity duration-300 max-w-64',
                    !allEncounters &&
                      hasAnyLinkedPlans &&
                      isLinkedPlanDataLoading &&
                      linkedPlans.length === 0 &&
                      'opacity-0 pointer-events-none',
                  )}
                  variant={
                    allEncounters
                      ? allEncountersHaveAnyLinkedPlans
                        ? 'outline'
                        : 'default'
                      : hasAnyLinkedPlans
                      ? 'outline'
                      : 'default'
                  }
                  onClick={openAddLinkDialog}
                >
                  Link raid plan
                </Button>
              ) : null}

              {canEditEvent && !allEncounters ? (
                <EventActivityHeaderContextMenu
                  activity={activity}
                  activities={activities}
                  allSelected={allSelectedForActivity}
                  trigger={
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      aria-label='Activity settings'
                    >
                      <Settings2Icon className='h-4 w-4' />
                    </Button>
                  }
                />
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {allEncounters ? null : showActivityNotes ? (
        <EventActivityNotesContent
          activity={activity}
          canEdit={canEditEvent && showActivityNotes}
          isEditing={isEditingActivityNotes}
          onStartEdit={() => setIsEditingActivityNotes(true)}
          onStopEdit={() => setIsEditingActivityNotes(false)}
        />
      ) : null}

      {allEncounters ? (
        <>
          {isLinkedPlanDataLoading &&
          encounterActivities.some(
            (entry) => (entry.linkedPlans ?? []).length > 0,
          ) &&
          !allEncountersHaveAnyLinkedPlans ? (
            <p className='text-muted-foreground flex justify-center text-center mt-6 mb-8'>
              <Loader2 className='h-8 w-8 animate-spin' />
            </p>
          ) : (
            encounterActivities.map((encounterActivity) => {
              const encounterLinkedPlanData = linkedPlanDataByActivityId?.get(
                encounterActivity.id,
              )
              const encounterLinkedPlans =
                encounterLinkedPlanData?.linkedPlans ?? []
              if (encounterLinkedPlans.length === 0) return null
              const encounterPlanOverrides =
                (encounterActivity.planOverrides as EventPlanOverridesByPlanId) ??
                {}

              return (
                <EventActivityLinkedPlanList
                  key={encounterActivity.id}
                  linkedPlans={encounterLinkedPlans}
                  selectedParticipants={
                    encounterLinkedPlanData?.selectedParticipants ?? []
                  }
                  planOverrides={encounterPlanOverrides}
                  canEdit={canEditPlans}
                  isSaving={isSaving}
                  reorderEnabled={false}
                  defaultExpanded={false}
                  titlePrefix={renderEncounterTitlePrefix(encounterActivity)}
                  onReplace={(planType, planId) =>
                    setLinkDialogState({
                      activityId: encounterActivity.id,
                      mode: { kind: 'replace', planType, planId },
                    })
                  }
                  onUnlink={(planType, planId) =>
                    unlinkPlan(encounterActivity, planType, planId)
                  }
                  onExportOverrides={(planId) => {
                    void exportEventActivityOverrides(
                      encounterActivity.id,
                      planId,
                    )
                  }}
                  onPlanOverridesChange={(nextPlanOverrides) =>
                    handlePlanOverridesChange(
                      encounterActivity,
                      nextPlanOverrides,
                    )
                  }
                />
              )
            })
          )}
        </>
      ) : activity.encounter ? (
        <>
          {hasAnyLinkedPlans &&
          isLinkedPlanDataLoading &&
          linkedPlans.length === 0 ? (
            <p className='text-muted-foreground flex justify-center text-center mt-6 mb-8'>
              <Loader2 className='h-8 w-8 animate-spin' />
            </p>
          ) : (
            <EventActivityLinkedPlanList
              linkedPlans={linkedPlans}
              selectedParticipants={selectedParticipants}
              planOverrides={planOverrides}
              canEdit={canEditPlans}
              isSaving={isSaving}
              reorderEnabled={canReorderLinkedPlans}
              onReplace={(planType, planId) =>
                setLinkDialogState({
                  activityId: activity.id,
                  mode: { kind: 'replace', planType, planId },
                })
              }
              onUnlink={(planType, planId) =>
                unlinkPlan(activity, planType, planId)
              }
              onMove={(planType, planId, direction) =>
                moveLinkedPlan(activity, planType, planId, direction)
              }
              onExportOverrides={(planId) => {
                void exportEventActivityOverrides(activity.id, planId)
              }}
              onPlanOverridesChange={(nextPlanOverrides) =>
                handlePlanOverridesChange(activity, nextPlanOverrides)
              }
            />
          )}
        </>
      ) : null}

      {(allEncounters ? allEncountersHaveAnyLinkedPlans : hasAnyLinkedPlans) ? (
        <div className='-mx-4 border-t border-border' />
      ) : null}

      <div className='flex items-center justify-between gap-2 max-w-5xl mx-auto mt-6'>
        <h4>Setup</h4>
        <EventActivitySelectionCount
          activity={activity}
          allEncounters={allEncounters}
          isIssueBadgePending={
            isPlanningInfoLoading ||
            (isLinkedPlanDataLoading && hasLinkedRaidPlanRefs(activity))
          }
        />
      </div>

      <EventActivityBlock
        activity={activity}
        applyToAllActivities={allEncounters}
      />

      {linkDialogState && linkDialogEncounterId ? (
        <LinkActivityPlanDialog
          open
          onOpenChange={(open) => {
            if (!open) setLinkDialogState(null)
          }}
          mode={linkDialogState.mode}
          raidPlans={candidateRaidPlans}
          visualPlans={candidateVisualPlans}
          linkedRaidPlanIds={
            allEncounters ? linkDialogLinkedRaidPlanIds : linkedRaidPlanIds
          }
          linkedVisualPlanIds={
            allEncounters ? linkDialogLinkedVisualPlanIds : linkedVisualPlanIds
          }
          encounterId={linkDialogEncounterId}
          difficulty={linkDialogActivity.difficulty}
          teamPath={team.path}
          encounters={
            allEncounters && linkDialogState.mode.kind === 'add'
              ? linkDialogEncounters
              : undefined
          }
          selectedEncounterId={linkDialogEncounterId}
          onSelectEncounter={
            allEncounters && linkDialogState.mode.kind === 'add'
              ? (encounterId) => {
                  const nextActivity = encounterActivities.find(
                    (entry) => entry.encounter?.id === encounterId,
                  )
                  if (!nextActivity) return
                  setLinkDialogState({
                    activityId: nextActivity.id,
                    mode: { kind: 'add' },
                  })
                }
              : undefined
          }
          onSelectRaidPlan={handleSelectRaidPlan}
          onSelectVisualPlan={handleSelectVisualPlan}
          onUnlinkRaidPlan={(planId) =>
            unlinkPlan(linkDialogActivity, 'raid', planId)
          }
          onUnlinkVisualPlan={(planId) =>
            unlinkPlan(linkDialogActivity, 'visual', planId)
          }
        />
      ) : null}
    </Card>
  )
}

export default EventActivityPlan
